import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Insurance } from '../services/types';
import BackButton from '../components/BackButton';
import ProgressBar from '../components/ProgressBar';
import { useAppState } from '../state/AppState';
import { useDataTransformation } from '../hooks/useDataTransformation';
import { getProviderNameFromAvailability } from '../services/availabilityDataService';
import { navigateToNextScreenFrom } from '../utils/smartNavigation';

/**
 * ScreenH - Insurance selection screen.
 *
 * Allows the user to select their insurance or indicate they will self-pay.
 */
const ScreenH: React.FC = () => {
  const navigate = useNavigate();
  const { dispatch, state } = useAppState();
  const { insurances, hasAvailabilityData } = useDataTransformation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [showModal, setShowModal] = useState(false);

  // Convert insurances to Insurance format and sort alphabetically
  const insuranceList: Insurance[] = insurances.map((insurance) => ({
    id: insurance,
    name: insurance
  })).sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    const loadInsurances = async () => {
      try {
        if (hasAvailabilityData && insurances.length > 0) {
          setLoading(false);
          return;
        }
        
        // Fallback to static data if no availability data
        setLoading(false);
      } catch (error) {
        setError('Failed to load insurance information');
        setLoading(false);
      }
    };

    // Add a small delay to ensure availability data is loaded
    const timeoutId = setTimeout(loadInsurances, 500);
    
    return () => clearTimeout(timeoutId);
  }, [hasAvailabilityData, insurances.length]);

  const handleToggle = (id: string) => {
    setSelected((prev) => {
      if (prev.includes(id)) {
        // Unselecting - always allowed
        return prev.filter((r) => r !== id);
      } else {
        // Selecting - only allowed if less than 3 are selected
        if (prev.length < 3) {
          const newSelected = [...prev, id];
          return newSelected;
        }
        return prev; // Don't add if already at max
      }
    });
  };

  const handleContinue = () => {
    const selectedNames = selected.map(id => {
      const insurance = insuranceList.find(ins => ins.id === id);
      return insurance ? insurance.name : id;
    });
    dispatch({ type: 'SET_INSURANCE', payload: selectedNames.join(', ') });
    
    // Use smart navigation to determine next screen from Screen H
    navigateToNextScreenFrom(state, 'h', navigate);
  };

  const handleOptionNotListed = () => {
    navigate('/v');
  };

  const handleInsuranceNotListed = () => {
    dispatch({ type: 'SET_INSURANCE', payload: 'My Insurance is Not Listed' });
    navigate('/u');
  };

  const handleSelfPay = () => {
    dispatch({ type: 'SET_INSURANCE', payload: 'I will Self Pay' });
    
    // Use smart navigation to determine next screen from Screen H
    navigateToNextScreenFrom(state, 'h', navigate);
  };

  return (
    <div className="screen-bg">
      <div className="main-content-container">
        <div className="mtg-back-btn-align">
          <BackButton onClick={() => navigate(-1)} />
        </div>
        <main className="mtg-screen-main" id="main-content">
          <div className="mtg-screen-card text-dark" style={{
            border: '1px solid #b5c7d6',
            borderRadius: 16,
            padding: '18px 18px 22px 18px',
            background: '#fff',
            marginBottom: 18,
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
          }}>
            <h2 className="mtg-screen-question mb-18" id="insurance-title">
              {getProviderNameFromAvailability(state.availabilities, null) ? `${getProviderNameFromAvailability(state.availabilities, null)}'s` : 'Your'}<br />accepted insurances.
            </h2>
            <div style={{ width: '100%', margin: '0 auto 18px auto', maxWidth: 280 }}>
              <div 
                className={`mtg-scrollable-list ${insuranceList.length > 5 ? 'mtg-scrollable-list-large' : ''}`}
                role="group"
                aria-labelledby="insurance-title"
                aria-describedby="insurance-description"
              >
              {loading ? (
                <div style={{ color: '#b5b5b5', fontWeight: 500 }} role="status" aria-live="polite">Loading...</div>
              ) : error ? (
                <div style={{ color: '#d32f2f', fontWeight: 500 }} role="alert" aria-live="assertive">{error}</div>
              ) : insuranceList.length === 0 ? (
                <div style={{ color: '#666', fontWeight: 500, textAlign: 'center', padding: '20px 0' }} role="status" aria-live="polite">
                  No insurances are available at this time.
                </div>
              ) : (
                <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {insuranceList.map((insurance) => {
                  const isSelected = selected.includes(insurance.id);
                  const isDisabled = !isSelected && selected.length >= 3;
                  
                  return (
                    <li key={insurance.id} className="mb-6 d-flex align-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={() => handleToggle(insurance.id)}
                        style={{ 
                          marginRight: 10, 
                          width: 18, 
                          height: 18,
                          minWidth: 18,
                          minHeight: 18,
                          maxWidth: 18,
                          maxHeight: 18,
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          border: isDisabled ? '0.5px solid #ccc' : '0.5px solid #e69e6d',
                          borderRadius: '2px',
                          backgroundColor: isSelected ? '#e69e6d' : (isDisabled ? '#f5f5f5' : 'transparent'),
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          position: 'relative',
                          boxSizing: 'border-box',
                          flexShrink: 0,
                          padding: 0,
                          margin: '0 10px 0 0',
                          outline: 'none',
                          verticalAlign: 'top'
                        }}
                        id={`insurance-${insurance.id}`}
                        aria-describedby={`insurance-${insurance.id}-status`}
                      />
                      <label
                        htmlFor={`insurance-${insurance.id}`}
                        style={{
                          fontWeight: isSelected ? 700 : 400,
                          color: isSelected ? '#1a355e' : (isDisabled ? '#999' : '#222'),
                          fontStyle: isSelected ? 'italic' : 'normal',
                          fontSize: '1rem',
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          paddingTop: "7px",
                          lineHeight: 1
                        }}
                      >
                        {insurance.name}
                      </label>
                      <span id={`insurance-${insurance.id}-status`} className="sr-only" role="status" aria-live="polite">
                        {isSelected ? `${insurance.name} selected` : 
                         isDisabled ? `${insurance.name} disabled - maximum of 3 selections allowed` :
                         `${insurance.name} not selected`}
                      </span>
                    </li>
                  );
                })}
              </ul>
              )}
              </div>
              
              {/* Informational message */}
              <div 
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  margin: '18px 0', 
                  padding: '0 12px',
                  cursor: 'pointer'
                }}
                onClick={() => setShowModal(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setShowModal(true);
                  }
                }}
                aria-label="Show insurance information"
              >
                <img 
                  src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTAiIGN5PSIxMCIgcj0iMTAiIGZpbGw9IiMxOTc2ZDIiLz4KPGNpcmNsZSBjeD0iMTAiIGN5PSI2IiByPSIxLjUiIGZpbGw9IndoaXRlIi8+CjxyZWN0IHg9IjguNSIgeT0iOCIgd2lkdGg9IjMiIGhlaWdodD0iNiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+Cg=="
                  alt="Information"
                  style={{
                    width: 20,
                    height: 20,
                    marginRight: 12,
                    flexShrink: 0
                  }}
                />
                <div style={{
                  fontSize: '0.75rem',
                  textDecoration: 'underline',
                  color: '#1976d2',
                  lineHeight: 1.4,
                  fontWeight: 400,
                  fontStyle: 'italic'
                }}>
                  Select all applicable insurance providers.<br />You may choose multiple options.
                </div>
              </div>
              
              <div style={{ padding: '0 12px' }}>
                <button
                  className="pill-btn-standard mb-18"
                  onClick={handleContinue}
                  disabled={selected.length === 0}
                  aria-describedby="continue-description"
                  style={{
                    opacity: selected.length === 0 ? 0.5 : 1,
                    cursor: selected.length === 0 ? 'not-allowed' : 'pointer'
                  }}
                >
                  Continue
                </button>
                <button
                  className="pill-btn-standard mb-14"
                  onClick={handleInsuranceNotListed}
                  disabled={selected.length > 0}
                  aria-label="My insurance is not listed"
                  style={{
                    opacity: selected.length > 0 ? 0.5 : 1,
                    cursor: selected.length > 0 ? 'not-allowed' : 'pointer'
                  }}
                >
                  My Insurance Is Not Listed
                </button>
                <button
                  className="pill-btn-standard mb-14"
                  onClick={handleSelfPay}
                  disabled={selected.length > 0}
                  aria-label="I will self pay"
                  style={{
                    opacity: selected.length > 0 ? 0.5 : 1,
                    cursor: selected.length > 0 ? 'not-allowed' : 'pointer'
                  }}
                >
                  I Will Self Pay
                </button>
                <div id="continue-description" className="sr-only">
                  {selected.length === 0 ? 'Please select at least one insurance option to continue' : `Continue with ${selected.length} selected insurance option${selected.length === 1 ? '' : 's'}`}
                </div>
              </div>
            </div>
           
            <ProgressBar progress={36} />
          </div>
          <div className="button-container">
            <button
              className="mtg-secondary-action-btn mb-18"
              onClick={handleOptionNotListed}
              aria-label="My option is not listed, contact us"
            >
              MY OPTION IS NOT LISTED
            </button>
          </div>
        </main>
      </div>
      
      {/* Floating Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setShowModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          aria-describedby="modal-message"
        >
          <div
            style={{
              backgroundColor: '#185F93',
              borderRadius: '12px',
              padding: '24px',
              paddingTop: '48px',
              maxWidth: '320px',
              width: '100%',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
              position: 'relative',
              color: '#fff'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowModal(false)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#fff',
                padding: '4px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'right',
                width: '32px',
                height: '32px'
              }}
              aria-label="Close modal"
            >
              ×
            </button>

            {/* Modal content */}
            <div style={{ textAlign: 'center' }}>
              <p
                id="modal-message"
                style={{
                  fontSize: '0.9rem',
                  lineHeight: '1.4',
                  marginBottom: '20px',
                  marginTop: 0,
                  color: '#fff'
                }}
              >
                Please select all applicable insurance providers. You may choose multiple options—be sure to include every insurance you have.<br />Failure to provide all insurance carriers may result in a greater financial responsibility.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScreenH; 